import { useState, useEffect, useCallback, useRef } from "react";

const useCRUDTable = ({ fetchFn, defaultSort, extraParams = {}, onError }) => {
  const [rows, setRows] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [input, setInput] = useState(defaultSort);
  const [orden, setOrden] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [highlightType, setHighlightType] = useState("");

  const extraParamsRef = useRef(extraParams);
  extraParamsRef.current = extraParams;

  const loadData = useCallback(
    async (overrideParams = {}) => {
      setCargando(true);
      try {
        const params = {
          ...extraParamsRef.current,
          page: overrideParams.page ?? page,
          cantidad: overrideParams.cantidad ?? rowsPerPage,
          input: overrideParams.input ?? input,
          orden: String(overrideParams.orden ?? orden),
          search:
            overrideParams.search !== undefined
              ? overrideParams.search
              : search,
          ...overrideParams,
        };
        console.log(params);

        const data = await fetchFn(params);
        if (data.pagination?.msg) {
          const retryParams = { ...params, page: 0 };
          const retryData = await fetchFn(retryParams);
          setRows(retryData.results || []);
          setPagination(retryData.pagination);
          setPage(0);
        } else {
          setRows(data.results || []);
          setPagination(data.pagination);
        }
      } catch (err) {
        console.log({ err });

        setRows([]);
        setPagination({ numRows: 0 });
        if (onError) onError(err);
      }
      setCargando(false);
    },
    [page, rowsPerPage, input, orden, search, fetchFn],
  );

  useEffect(() => {
    loadData();
  }, []);

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    setPage(0);
    setBuscando(true);
    loadData({ page: 0, search });
  };

  const handleSort = (colId) => {
    const newOrden = input === colId ? !orden : false;
    setInput(colId);
    setOrden(newOrden);
    setPage(0);
    loadData({ page: 0, input: colId, orden: newOrden });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    loadData({ page: newPage });
  };

  const handleChangeRowsPerPage = (e) => {
    const nuevaCantidad = parseInt(e.target.value, 10);
    setRowsPerPage(nuevaCantidad);
    setPage(0);
    loadData({ page: 0, cantidad: nuevaCantidad });
  };

  const handleRefresh = (extraOverrides = {}) => {
    setPage(0);
    setSearch("");
    setInput(defaultSort);
    setOrden(false);
    setBuscando(false);
    loadData({
      page: 0,
      search: "",
      input: defaultSort,
      orden: false,
      ...extraOverrides,
    });
  };

  const handleClearSearch = (extraOverrides = {}) => {
    setSearch("");
    setBuscando(false);
    setPage(0);
    setInput(defaultSort);
    setOrden(false);
    loadData({
      page: 0,
      search: "",
      input: defaultSort,
      orden: false,
      ...extraOverrides,
    });
  };

  const setHighlight = (id, type) => {
    setHighlightId(id);
    setHighlightType(type);
    setTimeout(() => {
      setHighlightId(null);
      setHighlightType("");
    }, 3000);
  };

  const totalPages = pagination?.numRows
    ? Math.ceil(pagination.numRows / rowsPerPage)
    : 0;

  return {
    rows,
    setRows,
    pagination,
    page,
    setPage,
    rowsPerPage,
    search,
    buscando,
    setBuscando,
    input,
    orden,
    cargando,
    highlightId,
    highlightType,
    totalPages,
    setSearch,
    loadData,
    handleSearch,
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage,
    handleRefresh,
    handleClearSearch,
    setHighlight,
  };
};

export default useCRUDTable;
